import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CopyIcon from '@mui/icons-material/ContentCopy';
import RevokeIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import { apiKeyService, ApiKey, CreateApiKeyResult } from '@services/apiKeyService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { formatDate as formatDate0 } from '@utils/format';

interface ApiKeysTabProps {
  orgId: string;
}

const formatDate = (dateStr: string | null | undefined): string => {
  return formatDate0(dateStr, { month: 'short', day: 'numeric', year: 'numeric', fallback: '-' });
};

function ApiKeysTab({ orgId }: ApiKeysTabProps) {
  const { t } = useTranslation();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  // REV-904: validated on submit, never by disabling Create.
  const [nameError, setNameError] = useState<string | null>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const [newKeyExpiry, setNewKeyExpiry] = useState('');

  // Created key display state
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke dialog state
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiKeyService.list(orgId);
      setKeys(data);
    } catch {
      setError(t('apiKeys.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async () => {
    // REV-904: Create is never disabled for a missing name. Submitting one says
    // what is missing, on the field, and moves focus there.
    if (!newKeyName.trim()) {
      setNameError(t('apiKeys.errors.nameRequired', 'A key name is required.'));
      nameInput.current?.focus();
      return;
    }

    setCreating(true);
    try {
      const result = await apiKeyService.create(orgId, {
        name: newKeyName.trim(),
        expiresAt: newKeyExpiry || undefined,
      });
      setCreatedKey(result);
      setCreateDialogOpen(false);
      setNewKeyName('');
      setNewKeyExpiry('');
      setNameError(null);
      void fetchKeys();
    } catch {
      setError(t('apiKeys.errors.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.rawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the text
    }
  };

  const handleRevoke = async () => {
    if (!keyToRevoke) return;

    setRevoking(true);
    try {
      await apiKeyService.revoke(orgId, keyToRevoke.id);
      setRevokeDialogOpen(false);
      setKeyToRevoke(null);
      void fetchKeys();
    } catch {
      setError(t('apiKeys.errors.revokeFailed'));
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">{t('apiKeys.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('apiKeys.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          {t('apiKeys.create')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Created key banner */}
      {createdKey && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setCreatedKey(null)}>
          <Typography variant="subtitle2" gutterBottom>
            {t('apiKeys.created.title')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('apiKeys.created.warning')}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={createdKey.rawKey}
            slotProps={{
              input: {
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? t('apiKeys.created.copied') : ''} open={copied}>
                      <IconButton
                        onClick={handleCopyKey}
                        size="small"
                        aria-label={t('apiKeys.created.copyKey', 'Copy API key')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Alert>
      )}

      {/* Keys list */}
      {keys.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('apiKeys.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('apiKeys.empty.description')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('apiKeys.empty.action')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('apiKeys.create')}
          </Button>
        </Box>
      ) : (
        <ScrollableTableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('apiKeys.fields.name')}</TableCell>
                <TableCell>{t('apiKeys.fields.prefix')}</TableCell>
                <TableCell>{t('apiKeys.fields.status')}</TableCell>
                <TableCell>{t('apiKeys.fields.lastUsed')}</TableCell>
                <TableCell>{t('apiKeys.fields.createdAt')}</TableCell>
                <TableCell>{t('apiKeys.fields.expiresAt')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {key.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {key.key_prefix}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`apiKeys.status.${key.status}`)}
                      size="small"
                      color={key.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {key.last_used_at ? formatDate(key.last_used_at) : t('apiKeys.never')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(key.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {key.expires_at ? formatDate(key.expires_at) : t('apiKeys.noExpiration')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {key.status === 'active' && (
                      <Tooltip title={t('apiKeys.revoke.title')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setKeyToRevoke(key);
                            setRevokeDialogOpen(true);
                          }}
                          aria-label={t('apiKeys.revoke.keyLabel', 'Revoke API Key {{name}}', {
                            name: key.name,
                          })}
                        >
                          <RevokeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
      )}

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setNewKeyName('');
          setNewKeyExpiry('');
          setNameError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('apiKeys.create')}</DialogTitle>
        <DialogContent>
          {/*
            autoFocus is correct here and must stay. When a modal dialog opens,
            focus has to move into it; leaving focus on the trigger behind the
            modal is the actual accessibility failure (WCAG 2.2 AA, focus order).
            MUI moves focus to the first focusable child, and this is it.

            jsx-a11y/no-autofocus is a blunt heuristic aimed at autofocus on page
            load, which steals focus the user did not ask to move. It cannot tell
            that this element is inside a Dialog, so it flags the one place the
            attribute is the right answer. Scoped disable rather than switching
            the rule off, so page-load autofocus stays reported.
          */}
          {/* eslint-disable jsx-a11y/no-autofocus */}
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label={t('apiKeys.fields.name')}
            required
            inputRef={nameInput}
            error={Boolean(nameError)}
            helperText={nameError ?? t('apiKeys.fields.nameHint')}
            value={newKeyName}
            onChange={(e) => {
              setNewKeyName(e.target.value);
              setNameError(null);
            }}
            disabled={creating}
          />
          {/* eslint-enable jsx-a11y/no-autofocus */}
          <TextField
            fullWidth
            margin="normal"
            label={t('apiKeys.fields.expiresAt')}
            helperText={t('apiKeys.fields.expiresAtHint')}
            type="date"
            value={newKeyExpiry}
            onChange={(e) => setNewKeyExpiry(e.target.value)}
            disabled={creating}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewKeyName('');
              setNewKeyExpiry('');
              setNameError(null);
            }}
            disabled={creating}
          >
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={24} /> : t('apiKeys.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => {
          setRevokeDialogOpen(false);
          setKeyToRevoke(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">{t('apiKeys.revoke.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('apiKeys.revoke.confirm')}</DialogContentText>
          {keyToRevoke && (
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
              {keyToRevoke.name} ({keyToRevoke.key_prefix}...)
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setRevokeDialogOpen(false);
              setKeyToRevoke(null);
            }}
            disabled={revoking}
          >
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={revoking}>
            {revoking ? <CircularProgress size={24} /> : t('apiKeys.revoke.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApiKeysTab;
